import PropTypes from 'prop-types';
import { useState } from 'react';
import constants from '../../config/constants';
import { noop } from '../../utils/noop';
import ColorSwatch from '../ui/ColorSwatch/ColorSwatch';
import { getInitialVisibleCount, shouldShowExpandable } from './ProductColorSwatches.helpers';
import { ProductColorSwatchesStyles } from './ProductColorSwatches.style';

const RegularColorSwatches = ({
  productId,
  colorVariants,
  type,
  handleColorSwatch,
  selectedColorIndex,
  size,
  appCtx,
  colorMetrics,
  variantSwatchType,
  inDeliveryOptions,
}) => {
  return (
    <div className={`color-swatch-container color-swatch-${type} mkp-color-swatch`}>
      <ColorSwatch
        colorVariants={colorVariants}
        handleColorSwatch={handleColorSwatch}
        selectedColorIndex={selectedColorIndex}
        productId={productId}
        key={productId}
        size={size}
        page={constants.PAGES.PDP}
        appCtx={appCtx}
        colorMetrics={colorMetrics}
        variantSwatchType={variantSwatchType}
        inDeliveryOptions={inDeliveryOptions}
      />
      <style jsx>{ProductColorSwatchesStyles}</style>
    </div>
  );
};

RegularColorSwatches.defaultProps = {
  handleColorSwatch: noop,
  selectedColorIndex: 0,
  variantSwatchType: undefined,
  inDeliveryOptions: false,
};

RegularColorSwatches.propTypes = {
  colorVariants: PropTypes.array.isRequired,
  productId: PropTypes.string.isRequired,
  handleColorSwatch: PropTypes.func,
  type: PropTypes.string.isRequired,
  selectedColorIndex: PropTypes.number,
  size: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  colorMetrics: PropTypes.object.isRequired,
  variantSwatchType: PropTypes.string,
  inDeliveryOptions: PropTypes.bool,
};

const ExpandableColorSwatches = ({
  productId,
  colorVariants,
  type,
  handleColorSwatch,
  selectedColorIndex,
  size,
  appCtx,
  colorMetrics,
  variantSwatchType,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isModernLineContainerEnabled = !!appCtx?.siteConfig?.toggles?.isModernLineContainerEnabled;
  const initialVisibleCount = getInitialVisibleCount(
    appCtx.deviceType,
    variantSwatchType,
    isModernLineContainerEnabled
  );
  const visibleCount = isExpanded ? colorVariants.length : initialVisibleCount;
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="expandable-wrapper">
      <div className={`color-swatch-container color-swatch-${type}`}>
        <ColorSwatch
          colorVariants={colorVariants}
          handleColorSwatch={handleColorSwatch}
          selectedColorIndex={selectedColorIndex}
          productId={productId}
          key={productId}
          size={size}
          page={constants.PAGES.PDP}
          appCtx={appCtx}
          colorMetrics={colorMetrics}
          variantSwatchType={variantSwatchType}
          showExpandButton={false}
          visibleCount={visibleCount}
        />
      </div>
      {colorVariants.length > initialVisibleCount && (
        <button type="button" id="testId-expand-toggle" className="expand-swatch" onClick={toggleExpand}>
          {!isExpanded ? (
            <>
              <span className="plus-icon" />
              <span className="collapse-text">
                Mostrar más colores ({colorVariants.length - initialVisibleCount} más)
              </span>
            </>
          ) : (
            <>
              <span className="minus-icon" />
              <span className="collapse-text">Mostrar menos colores</span>
            </>
          )}
        </button>
      )}
      <style jsx>{ProductColorSwatchesStyles}</style>
    </div>
  );
};

ExpandableColorSwatches.propTypes = {
  colorVariants: PropTypes.array.isRequired,
  productId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  handleColorSwatch: PropTypes.func.isRequired,
  selectedColorIndex: PropTypes.number.isRequired,
  size: PropTypes.string.isRequired,
  appCtx: PropTypes.object.isRequired,
  colorMetrics: PropTypes.object.isRequired,
  variantSwatchType: PropTypes.string.isRequired,
};

const ProductColorSwatches = ({
  colorVariants,
  productId,
  type,
  selectedColorSwatchIndex,
  colorSwatchHandler,
  size,
  appCtx,
  colorMetrics,
  variantSwatchType,
  inDeliveryOptions,
}) => {
  const { deviceType } = appCtx;
  const isModernLineContainerEnabled = !!appCtx?.siteConfig?.toggles?.isModernLineContainerEnabled;

  // Using helper to expandable
  const isRegularColorSwatch = !shouldShowExpandable(
    variantSwatchType,
    colorVariants,
    deviceType,
    isModernLineContainerEnabled
  );

  return isRegularColorSwatch ? (
    <RegularColorSwatches
      productId={productId}
      colorVariants={colorVariants}
      type={type}
      handleColorSwatch={colorSwatchHandler}
      selectedColorIndex={selectedColorSwatchIndex}
      size={size}
      appCtx={appCtx}
      colorMetrics={colorMetrics}
      variantSwatchType={variantSwatchType}
      inDeliveryOptions={inDeliveryOptions}
    />
  ) : (
    <ExpandableColorSwatches
      productId={productId}
      colorVariants={colorVariants}
      type={type}
      handleColorSwatch={colorSwatchHandler}
      selectedColorIndex={selectedColorSwatchIndex}
      size={size}
      appCtx={appCtx}
      colorMetrics={colorMetrics}
      variantSwatchType={variantSwatchType}
    />
  );
};

ProductColorSwatches.defaultProps = {
  type: constants.PRODUCT_CONSTANTS.HARDLINE,
  selectedColorSwatchIndex: 0,
  colorSwatchHandler: noop,
  size: 'medium',
  variantSwatchType: undefined,
  inDeliveryOptions: false,
};

ProductColorSwatches.propTypes = {
  colorVariants: PropTypes.array.isRequired,
  productId: PropTypes.string.isRequired,
  type: PropTypes.string,
  selectedColorSwatchIndex: PropTypes.number,
  colorSwatchHandler: PropTypes.func,
  size: PropTypes.oneOf(['medium', 'large']),
  appCtx: PropTypes.object.isRequired,
  colorMetrics: PropTypes.object.isRequired,
  variantSwatchType: PropTypes.string,
  inDeliveryOptions: PropTypes.bool,
};

export default ProductColorSwatches;
